import ChartRadial from '@/components/charts/RadialNew';
import { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import customerAvatar from '@/imgs/customer-avatar.png';
import { splitAmount } from '@/lib/utils';
import overviewIcon from '@/imgs/overview-icon.png';
import aiAvatar from '@/imgs/ai-avatar.png';
import vipBadge from '@/imgs/vip-badge.png';
import type { RadialData } from '@/components/charts/RadialNew';
interface CustomerOverviewProps {
  taskId?: string;
  userProfile?: any;
  extendedProfile?: any;
  portfolioDiagnosis?: any;
}

interface UserInfo {
  user_tags: string[];
  profile_summary: string;
  customer_name: string;
  risk_level: string;
  investment_experience: string;
  total_assets: string;
}

const CustomerOverview = forwardRef<any, CustomerOverviewProps>(
  ({ userProfile, extendedProfile, portfolioDiagnosis }, ref) => {
    const [userInfo, setUserInfo] = useState<UserInfo>({
      user_tags: [],
      profile_summary: '',
      customer_name: '',
      risk_level: '',
      investment_experience: '',
      total_assets: ''
    });

    // 权益、固收、现金、固收型
    // const palette = ['#FF7B52', '#F8467B', '#1677FF'];

    const [assetData, setAssetData] = useState<RadialData[]>([
      {
        value: 0,
        color: '#FF7B52',
        name: '权益类'
      },
      {
        value: 0,
        color: '#F8467B',
        name: '固收类'
      },
      {
        value: 0,
        color: '#1677FF',
        name: '货币类'
      }
    ]);

    useImperativeHandle(ref, () => ({
      init: () => {}
    }));

    useEffect(() => {
      const userObj = {
        user_tags: [],
        profile_summary: '',
        customer_name: '',
        risk_level: '',
        investment_experience: '',
        total_assets: ''
      } as UserInfo;

      if (extendedProfile) {
        const { customerInfo } = extendedProfile;
        const info = (customerInfo && customerInfo[0]) || {};
        userObj.customer_name = info.customer_name || '';
        userObj.risk_level = info.risk_level || '';
        userObj.investment_experience = info.investment_experience || '';
        userObj.total_assets = info.total_assets || '';
      }

      if (portfolioDiagnosis) {
        // todo
        const { aggressive_before, stable_before, cash_before } = portfolioDiagnosis;
        setAssetData([
          {
            value: splitAmount(aggressive_before).numInt,
            color: '#FF7B52',
            name: '权益类'
          },
          {
            value: splitAmount(stable_before).numInt,
            color: '#F8467B',
            name: '固收类'
          },
          {
            value: splitAmount(cash_before).numInt,
            color: '#1677FF',
            name: '货币类'
          }
        ]);
      }

      const userTagsValue = (userProfile.user_tags || '').split('、').map((tag: String) => tag);
      userObj.user_tags = userTagsValue;
      userObj.profile_summary = userProfile.profile_summary || '';
      setUserInfo(userObj);
      console.log('userProfile', userProfile);
      console.log('extendedProfile', extendedProfile);
      console.log('发生变化了');
    }, [userProfile, extendedProfile]);

    return (
      <>
        {/* 标题区域 */}
        <div className="mb-3 flex items-center gap-1">
          <div className="h-6 w-6">
            <img src={overviewIcon} alt="总览" className="h-full w-full object-cover" />
          </div>
          <span className="text-base font-semibold text-black">客户概览</span>
        </div>
        <div className="rounded-lg border border-gray-100 bg-white p-3">
          <div className="flex gap-2">
            {/* 客户信息卡片 */}
            <div className="mb-3 w-[65%] rounded-lg border border-gray-100 bg-white p-3">
              <div className="relative">
                <div>
                  <div className="mb-2 flex gap-2">
                    {/* 客户头像和VIP标识 */}
                    <div className="relative">
                      <div className="h-10 w-10 overflow-hidden rounded-full bg-gray-200">
                        <img
                          src={customerAvatar}
                          alt="客户头像"
                          className="h-full w-full object-cover"
                        />
                      </div>
                    </div>

                    {/* 客户姓名和VIP标识 */}
                    <div className="flex items-center gap-1">
                      <span className="text-[14px] font-normal leading-[22px] text-[#1F1F1F]">
                        {userInfo.customer_name}
                      </span>
                      <div className="order-1 h-4 w-10 flex-none">
                        <img src={vipBadge} alt="VIP" className="h-full w-full object-cover" />
                      </div>
                    </div>
                  </div>
                  {/* 总资产信息 */}
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-[14px] font-normal leading-[22px] text-[#1F1F1F]">
                      总资产
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-2xl font-semibold text-transparent">
                        {splitAmount(userInfo.total_assets).num}
                      </span>
                      <span className="text-sm text-gray-800">
                        {splitAmount(userInfo.total_assets).unit}
                      </span>
                    </div>
                  </div>
                  {/* 风险等级和投资经验 */}
                  <div className="mb-3 text-left text-xs text-gray-500">
                    风险等级: <span> {userInfo.risk_level}</span> •{' '}
                    <span> {userInfo.investment_experience}</span>
                  </div>

                  {/* 客户标签 */}
                  <div className="text-left">
                    {(userInfo.user_tags || []).map(tag => (
                      <span
                        key={tag}
                        className="mr-1 scale-[0.8] whitespace-nowrap rounded bg-blue-50 p-1 text-xs text-gray-800"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* 资产占比图表 */}
            <div className="mb-3 w-[35%] rounded-lg border border-gray-100 bg-white p-3">
              <div className="mb-2 text-center">
                <span className="text-sm font-semibold text-gray-800">资产占比</span>
              </div>

              {/* 同心环图表 */}
              <div className="mb-2 flex h-[150px] w-full justify-center">
                <ChartRadial
                  data={assetData}
                  legendPosition="bottom"
                  innerRadius={40}
                  outerRadius={60}
                  cx="50%"
                  cy="40%"
                />
              </div>

              {/* 图例（与颜色顺序一致：橙、粉、蓝、紫） */}
              {/* <div className="grid grid-cols-2 gap-2">
                {assetData.map(ring => (
                  <div key={ring.fill} className="flex items-center">
                    <div style={{ backgroundColor: ring.fill }} className="w-4 h-2 rounded-[2px]" />
                    <span className="text-xs text-gray-800 whitespace-nowrap scale-[0.8]">
                      {ring.name}
                    </span>
                  </div>
                ))}
              </div> */}
            </div>
          </div>

          {/* AI总结区域 */}
          <div className="relative">
            {/* 装饰性背景 */}
            <div className="h-18 absolute right-0 top-0 -z-10 w-40 rounded-l-full rounded-br-full bg-gradient-to-br from-purple-50 to-blue-50" />

            {/* AI头像和总结 */}
            <div className="flex items-center">
              <div className="h-18 w-14">
                <img src={aiAvatar} alt="AI助手" className="h-full w-full object-cover" />
              </div>

              <span className="ml-4 mt-[-10px] bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-base font-semibold text-transparent">
                AI总结
              </span>
            </div>

            {/* AI总结内容 */}
            <div className="relative mt-[-20px] rounded-lg border border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50 p-3 shadow-inner">
              <p className="text-left text-xs leading-relaxed text-gray-800">
                {userInfo.profile_summary}
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }
);

CustomerOverview.displayName = 'CustomerOverview';

export default CustomerOverview;
